import { useEffect } from "react";

const TripHighlightsSection = ({ dayWiseItinerary, destinationImages, activeDay, setActiveDay, setExpandedDays }) => {
  const handleDayClick = (index) => {
    setActiveDay(index);
    if (setExpandedDays) {
      setExpandedDays({ [index]: true });
    }
    if (window.innerWidth < 768) {
      const element = document.getElementById(`highlight-image-${index}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  };
  if (!dayWiseItinerary || dayWiseItinerary.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6 lg:p-8 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">Trip Highlights</h2>
        <span className="text-xs md:text-sm font-semibold text-teal-700 bg-teal-50 px-3 py-1.5 rounded-full border border-teal-100">
          {dayWiseItinerary.length} Days Trip
        </span>
      </div>

      <div className="flex overflow-x-auto md:flex-wrap gap-2 md:gap-3 mb-5 pt-2 pb-2 px-1 -mx-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {dayWiseItinerary.map((day, index) => (
          <button
            key={index}
            onClick={() => handleDayClick(index)}
            className={`flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 cursor-pointer rounded-full font-medium text-[13px] md:text-sm whitespace-nowrap transition-all duration-300 flex-shrink-0 ${
              activeDay === index
                ? "bg-gradient-to-r from-teal-600 to-teal-500 text-white shadow-md shadow-teal-200/50 ring-2 ring-offset-2 ring-teal-500"
                : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300 shadow-sm"
            }`}
          >
            <div className={`w-1.5 h-1.5 rounded-full ${activeDay === index ? "bg-white" : "bg-teal-500"}`} />
            Day {index + 1}
          </button>
        ))}
      </div>

      <div className="flex overflow-x-auto md:grid md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 pb-4 px-1 pt-1 -mx-1 snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {dayWiseItinerary.map((day, index) => (
          <button
            key={index}
            id={`highlight-image-${index}`}
            onClick={() => handleDayClick(index)}
            className={`w-[85%] sm:w-[60%] md:w-auto flex-shrink-0 snap-center relative rounded-2xl overflow-hidden aspect-[4/3] md:aspect-square group text-left transition-all duration-500 cursor-pointer ${
              activeDay === index 
                ? 'ring-2 ring-teal-500 ring-offset-2 scale-[1.02] shadow-xl shadow-teal-900/10' 
                : 'hover:scale-[1.01] hover:shadow-md opacity-90 hover:opacity-100 border border-gray-100'
            }`}
          >
            <img
              src={day.image?.url || destinationImages?.[index % destinationImages?.length]?.url}
              alt={day.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
            />
            
            <div className={`absolute inset-0 transition-all duration-500 ${
              activeDay === index
                ? 'bg-gradient-to-t from-teal-950/95 via-teal-900/50 to-transparent'
                : 'bg-gradient-to-t from-black/90 via-black/40 to-transparent'
            }`} />
            
            <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 pr-12">
                <span className="px-2 py-1 bg-black/40 backdrop-blur-md text-white border border-white/20 shadow-sm text-[10px] font-semibold rounded-md">
                  Day {index + 1}
                </span>
                {day.activities && day.activities.length > 0 && (
                   <span className="px-2 py-1 bg-white/20 backdrop-blur-md text-white border border-white/20 shadow-sm text-[10px] font-medium rounded-md flex items-center gap-1">
                     <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                     </svg>
                     {day.activities.length} Act.
                   </span>
                )}
            </div>

            {activeDay === index && (
              <div className="absolute top-3 right-3">
                <div className="bg-teal-500 text-white shadow-sm p-1.5 rounded-full backdrop-blur-md border border-teal-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            )}

            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 transform transition-transform duration-500 group-hover:translate-y-[-4px]">
              <h3 className="text-[15px] md:text-[17px] font-bold text-white mb-1.5 drop-shadow-md leading-tight">
                {day.title?.split('-')[0]?.trim() || day.title}
              </h3>
              
              <p className="text-[11px] md:text-xs text-white/90 line-clamp-2 md:line-clamp-3 drop-shadow leading-relaxed mb-2.5">
                {day.overview || day.description || (day.highlights ? day.highlights.slice(0, 3).join(' • ') : '')}
              </p>

              {day.highlights && day.highlights.length > 0 && (
                <div className="flex items-center gap-2 overflow-hidden bg-black/20 p-1.5 rounded-lg border border-white/10 backdrop-blur-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0 shadow-[0_0_5px_rgba(45,212,191,0.8)]"></div>
                    <p className="text-[10px] text-teal-50 truncate font-medium tracking-wide">
                        {day.highlights[0]}
                    </p>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default TripHighlightsSection;
